import React, { useState } from 'react';
import { Upload, Video, Users, User, Globe, FileVideo, Link, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { value: 'custom', label: 'Custom Content' },
  { value: 'mental-training', label: 'Mental Training' },
  { value: 'technique', label: 'Technique' },
  { value: 'motivation', label: 'Motivation' },
  { value: 'sport-psychology', label: 'Sport Psychology' },
  { value: 'meditation', label: 'Meditation' },
  { value: 'mindfulness', label: 'Mindfulness' },
  { value: 'yoga', label: 'Yoga' },
];

const VideoUpload = ({ user, onVideoUploaded }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [uploadMode, setUploadMode] = useState('url'); // 'url' or 'file'
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    video_url: '',
    thumbnail_url: '',
    duration_minutes: '',
    category: 'custom',
    tags: '',
    target_audience: 'all',
    target_ids: []
  });

  const resetForm = () => {
    setFormData({
      title: '', description: '', video_url: '', thumbnail_url: '',
      duration_minutes: '', category: 'custom', tags: '',
      target_audience: 'all', target_ids: []
    });
    setSelectedFile(null);
    setUploadProgress(0);
    setUploadMode('url');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 500 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File too large. Maximum 500MB allowed.');
      return;
    }

    const validTypes = [
      'video/mp4', 'video/webm', 'video/ogg', 'video/quicktime',
      'audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/flac'
    ];
    if (!validTypes.includes(file.type)) {
      toast.error('Unsupported file type. Use MP4, WebM, OGG, MOV, MP3, WAV, or FLAC.');
      return;
    }

    setSelectedFile(file);
    if (!formData.title) {
      const name = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
      setFormData(prev => ({ ...prev, title: name }));
    }
  };

  const handleSubmitFile = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    setLoading(true);
    setUploadProgress(0);

    try {
      const fd = new FormData();
      fd.append('file', selectedFile);
      fd.append('title', formData.title);
      fd.append('description', formData.description);
      fd.append('category', formData.category);
      fd.append('tags', formData.tags);
      if (formData.duration_minutes) fd.append('duration_minutes', formData.duration_minutes);
      fd.append('target_audience', formData.target_audience);
      fd.append('target_ids', formData.target_ids.join(','));

      const response = await axios.post(`${API}/videos/file-upload`, fd, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(pct);
        }
      });

      toast.success('File uploaded successfully!');
      resetForm();
      setIsOpen(false);
      if (onVideoUploaded) onVideoUploaded(response.data);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Upload failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitUrl = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()) : [],
        target_ids: formData.target_ids || []
      };

      const response = await axios.post(`${API}/videos/upload`, submitData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      toast.success('Video uploaded successfully!');
      resetForm();
      setIsOpen(false);
      if (onVideoUploaded) onVideoUploaded(response.data);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Upload failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!user || !['coach', 'sport-performance-consultant', 'system-admin'].includes(user.role)) {
    return null;
  }

  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <>
      <button
        data-testid="upload-video-btn"
        onClick={() => setIsOpen(true)}
        className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
      >
        <Upload className="w-4 h-4" />
        <span>Upload Video</span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                  <Video className="w-5 h-5 text-emerald-600" />
                  <span>Upload Content</span>
                </h2>
                <button
                  data-testid="close-upload-modal"
                  onClick={() => { setIsOpen(false); resetForm(); }}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  disabled={loading}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Upload Mode Toggle */}
              <div className="flex gap-2 mb-6 p-1 bg-gray-100 rounded-lg">
                <button
                  data-testid="upload-mode-url"
                  type="button"
                  onClick={() => setUploadMode('url')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-medium transition-all ${
                    uploadMode === 'url'
                      ? 'bg-white text-emerald-700 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Link className="w-4 h-4" />
                  URL Link
                </button>
                <button
                  data-testid="upload-mode-file"
                  type="button"
                  onClick={() => setUploadMode('file')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-medium transition-all ${
                    uploadMode === 'file'
                      ? 'bg-white text-emerald-700 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <FileVideo className="w-4 h-4" />
                  Upload File
                </button>
              </div>

              <form onSubmit={uploadMode === 'file' ? handleSubmitFile : handleSubmitUrl} className="space-y-4">
                {/* File upload area */}
                {uploadMode === 'file' && (
                  <div>
                    {!selectedFile ? (
                      <label
                        data-testid="file-drop-zone"
                        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/50 transition-all block"
                      >
                        <FileVideo className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-sm font-medium text-gray-700 mb-1">
                          Click to select a video or audio file
                        </p>
                        <p className="text-xs text-gray-500">
                          MP4, WebM, MOV, MP3, WAV, FLAC — Max 500MB
                        </p>
                        <input
                          type="file"
                          accept="video/*,audio/*"
                          onChange={handleFileSelect}
                          className="hidden"
                          disabled={loading}
                        />
                      </label>
                    ) : (
                      <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileVideo className="w-8 h-8 text-emerald-600" />
                          <div>
                            <p className="text-sm font-medium text-gray-900 truncate max-w-xs">{selectedFile.name}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(selectedFile.size)}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSelectedFile(null)}
                          className="text-gray-400 hover:text-red-500 p-1"
                          disabled={loading}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}

                    {/* Upload Progress Bar */}
                    {loading && uploadProgress > 0 && (
                      <div className="mt-3">
                        <div className="flex justify-between text-xs text-gray-600 mb-1">
                          <span>Uploading...</span>
                          <span>{uploadProgress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* URL input */}
                {uploadMode === 'url' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Video URL *</label>
                    <input
                      data-testid="video-url-input"
                      type="url"
                      value={formData.video_url}
                      onChange={(e) => handleInputChange('video_url', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="https://youtube.com/watch?v=..."
                      required
                      disabled={loading}
                    />
                    <p className="text-xs text-gray-500 mt-1">YouTube, Vimeo, or direct video URLs</p>
                  </div>
                )}

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input
                    data-testid="video-title-input"
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Enter video title"
                    required
                    disabled={loading}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                  <textarea
                    data-testid="video-description-input"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={2}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Describe the content"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Duration (min)</label>
                    <input
                      data-testid="video-duration-input"
                      type="number"
                      value={formData.duration_minutes}
                      onChange={(e) => handleInputChange('duration_minutes', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="15"
                      min="1"
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      data-testid="video-category-select"
                      value={formData.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      disabled={loading}
                    >
                      {categories.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma separated)</label>
                  <input
                    data-testid="video-tags-input"
                    type="text"
                    value={formData.tags}
                    onChange={(e) => handleInputChange('tags', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="confidence, pre-game, focus"
                    disabled={loading}
                  />
                </div>

                {/* Thumbnail URL (URL mode only) */}
                {uploadMode === 'url' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Thumbnail URL (optional)</label>
                    <input
                      type="url"
                      value={formData.thumbnail_url}
                      onChange={(e) => handleInputChange('thumbnail_url', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="https://example.com/thumbnail.jpg"
                      disabled={loading}
                    />
                  </div>
                )}

                {/* Target Audience */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Audience</label>
                  <div className="flex gap-4">
                    {[
                      { value: 'all', icon: Globe, label: 'All' },
                      { value: 'specific-team', icon: Users, label: 'Team' },
                      { value: 'specific-athlete', icon: User, label: 'Athlete' }
                    ].map(opt => (
                      <label key={opt.value} className="flex items-center gap-1.5 text-sm">
                        <input
                          type="radio"
                          name="target_audience"
                          value={opt.value}
                          checked={formData.target_audience === opt.value}
                          onChange={(e) => handleInputChange('target_audience', e.target.value)}
                          className="text-emerald-600"
                          disabled={loading}
                        />
                        <opt.icon className="w-3.5 h-3.5 text-gray-500" />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Approval Notice */}
                {user.role !== 'system-admin' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs text-amber-800">
                      Videos require admin approval before becoming visible to athletes.
                    </p>
                  </div>
                )}

                {/* Buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => { setIsOpen(false); resetForm(); }}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-lg text-sm font-medium transition-colors"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    data-testid="submit-upload-btn"
                    type="submit"
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    disabled={loading || (uploadMode === 'file' && !selectedFile)}
                  >
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {loading ? (uploadMode === 'file' ? 'Uploading...' : 'Saving...') : 'Upload'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VideoUpload;
